import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Handshake } from "lucide-react";

const MILESTONES = [
  { when: "12+ months", t: "Set the date & budget", d: "Book the venue and your planner." },
  { when: "9 months", t: "Build the dream team", d: "Photographer, florist, music, attire." },
  { when: "6 months", t: "Invite your people", d: "Guest list, save-the-dates, website live." },
  { when: "3 months", t: "Lock the details", d: "Menu, cake, vows, final fittings." },
  { when: "1 month", t: "Final RSVPs & seating", d: "Balances paid, seating chart done." },
  { when: "Wedding day", t: "Say \"I do\"", d: "Run of show, live stream, guest games." },
  { when: "Forever", t: "Keep every memory", d: "Photos, blessings and anniversaries." },
];

const Item = ({ m, i }) => (
  <motion.li initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: i * 0.08, duration: 0.5 }}
    className="snap-start shrink-0 w-[70%] sm:w-[40%] lg:w-auto relative pt-10" data-testid={`milestone-${i}`}>
    <motion.span initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.12, type: "spring", stiffness: 400, damping: 18 }}
      className="absolute top-0 left-0 w-8 h-8 rounded-full bg-[var(--gold)] text-white flex items-center justify-center ring-4 ring-[var(--bg)] z-10"><Check className="w-4 h-4" /></motion.span>
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#8a6d35]">{m.when}</p>
    <p className="font-serif text-xl mt-1">{m.t}</p>
    <p className="text-sm text-[var(--muted)] mt-1">{m.d}</p>
  </motion.li>
);

export default function MilestoneTrack() {
  return (
    <section id="milestones" className="section-pad bg-[var(--bg)]" data-testid="milestone-track">
      <div className="container-wide px-5 md:px-12">
        <div className="grid lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-7">
            <p className="font-script text-[var(--gold)] text-4xl">From "yes" to "I do"</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-1">Every milestone, tracked.</h2>
            <p className="text-[var(--muted)] mt-4 max-w-xl">Your virtual wedding planning tool counts down every task to your date and keeps the whole plan organized in one place.</p>
          </div>
          <div className="lg:col-span-5 bg-[var(--ink)] text-white rounded-3xl p-5 flex gap-4 items-start" data-testid="planner-friendly-callout">
            <span className="w-11 h-11 rounded-2xl bg-[#C8A96B]/20 flex items-center justify-center shrink-0"><Handshake className="w-5 h-5 text-[var(--gold)]" /></span>
            <div>
              <p className="font-serif text-xl">Your planner stays. The chaos goes.</p>
              <p className="text-sm text-white/65 mt-1">We don't replace wedding planners. Invite yours in and you'll both work from the same organized plan.</p>
            </div>
          </div>
        </div>
        <div className="relative mt-12">
          <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: "easeInOut" }}
            className="hidden lg:block absolute top-4 left-4 right-4 h-px bg-[#C8A96B]/60 origin-left" />
          <ol className="flex lg:grid lg:grid-cols-7 gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2">
            {MILESTONES.map((m, i) => <Item key={m.t} m={m} i={i} />)}
          </ol>
        </div>
        <div className="flex flex-wrap gap-3 mt-10">
          <Link to="/signup?role=couple" className="btn-gold" data-testid="milestones-start-btn">Start your milestone plan <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/demo/timeline" className="btn-outline-gold" data-testid="milestones-demo-btn">See the timeline demo</Link>
          <Link to="/for-planners" className="btn-outline-gold" data-testid="milestones-planners-btn">I'm a planner</Link>
        </div>
      </div>
    </section>
  );
}
